import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, Inject, OnDestroy, PLATFORM_ID, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { L10nLocale, L10N_LOCALE } from 'angular-l10n';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BaseService } from 'src/app/core/services/base.service';
import { MessageBusService, PublicProfileHeaderVisibilityChangeMessage } from 'src/app/core/services/message-bus.service';
import { RouteDialogService } from 'src/app/core/services/route-dialog.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { DoctorMessageDialogComponent } from 'src/app/shared/components/dialogs/doctor-message-dialog/doctor-message-dialog.component';
import { PaymentRouteDialogModel } from 'src/app/shared/components/dialogs/route-dialog/route-dialog.component';
import { PublicProfileAvatarComponentModel } from 'src/app/shared/components/public-profile-avatar/public-profile-avatar.component';
import { FeatureConceptDto } from 'src/app/shared/components/search-results/provider-card/provider-card.component';
import { ComponentLookup } from 'src/app/shared/decorators/component-lookup.decorator';
import { Direction } from 'src/app/shared/enums/direction.enum';
import { IControlComponent } from 'src/app/shared/interfaces/control-component';
import { DialogData } from 'src/app/shared/models/dialog-data.model';
import { FormComponentData } from 'src/app/shared/models/people/form-control.model';
import { PublicProfileType } from 'src/app/shared/models/people/profile-search-results.model';
import { PublicProfile, SocialNetworkPublicProfile } from 'src/app/shared/models/people/public-profile.model';
import { OnlinePaymentModel } from 'src/app/shared/models/workflow/models/online-payment.model';

@Component({
  selector: 'app-doctor-header',
  templateUrl: './doctor-header.component.html',
  styleUrls: ['./doctor-header.component.css'],
  encapsulation: ViewEncapsulation.None
})
@ComponentLookup('DoctorHeaderComponent')
export class DoctorHeaderComponent implements AfterViewInit, OnDestroy, IControlComponent  {
  data: FormComponentData;

  private ngUnsubscribe = new Subject();

  publicProfile: PublicProfile = new PublicProfile();
  showCard: boolean = false;
  specialties: string;
  subspecialties: string;
  initials: any;
  avatarModel: PublicProfileAvatarComponentModel;
  instagramSocialMedia: any;

  isVerifiedDoctor = {isVerified: false, license: ''}

  isCliniwebCompany:boolean = false;

  constructor(
    protected messageBusService: MessageBusService,
    private dialog: MatDialog,
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private utilsService: UtilsService,
    private baseService: BaseService,
    private routeDialogService: RouteDialogService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.isCliniwebCompany = this.baseService.isCliniwebCompany();
  }
  
  ngAfterViewInit (): void {
    this.messageBusService.onSendPublicProfile()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(message => {        

        this.publicProfile = message.publicProfile;
        this.specialties = this.getSpecialties();
        this.subspecialties = this.getSubspecialties();
        this.initials = this.utilsService.getPersonInitials(this.publicProfile.idPerfilPublico, this.publicProfile.nombrePersona);

        this.showCard = true;
        
        this.avatarModel = new PublicProfileAvatarComponentModel();
        this.avatarModel.personId = this.publicProfile.id;
        this.avatarModel.avatarUrl = this.publicProfile.ubicacionFoto;
        this.avatarModel.fullName = this.publicProfile.nombrePersona;
        this.avatarModel.sex = this.publicProfile.sexo;
        this.avatarModel.profileType = this.publicProfile.tipoPerfil;

        if (this.publicProfile.redesSociales && this.publicProfile.redesSociales.length > 0) {
          this.instagramSocialMedia = this.publicProfile.redesSociales.find(r => r.tipo == 28);
          this.publicProfile.redesSociales = this.publicProfile.redesSociales.filter(r => r.tipo != 28);
        }
      });

    this.messageBusService.isVerifiedDoctor().subscribe(x => this.isVerifiedDoctor = x);
      
    this.baseService.sendComponentFinishLoadingMessage(this.data);    

    this.messageBusService.onPageScroll()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(scrollMessage => {
        let headerEl = document.getElementsByClassName("doctor-header-container")[0] as HTMLElement;
        let offset = headerEl? headerEl.offsetHeight + headerEl.offsetTop : 0;
        
        if (scrollMessage.direction == Direction.Down && (scrollMessage.offset + scrollMessage.containerTopPosition) > offset) {
          this.messageBusService.sendPublicProfileHeaderVisibilityChange(
            new PublicProfileHeaderVisibilityChangeMessage(false, this.publicProfile, this.avatarModel, scrollMessage.routeDialogScroll));
        }
        else if (scrollMessage.direction == Direction.Up && (scrollMessage.offset + scrollMessage.containerTopPosition) < offset) {
          this.messageBusService.sendPublicProfileHeaderVisibilityChange(
            new PublicProfileHeaderVisibilityChangeMessage(true, this.publicProfile, this.avatarModel, scrollMessage.routeDialogScroll));
        }
      }
    );

    this.messageBusService.onPublicProfileAction()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(msg => {
        if (msg.appointmentClick)
          this.onAppointmentBtnClick();
        else if (msg.paymentClick)
          this.gotoPayment();
      }
    );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  } 

  getSpecialties() {
    if (!this.publicProfile.especialidades || this.publicProfile.especialidades.length == 0)
      return "";

    return this.publicProfile.especialidades.map(s => s.objetoDetalle).join(", ");
  }

  getSubspecialties() {
    if (!this.publicProfile.subEspecialidades || this.publicProfile.subEspecialidades.length == 0)
      return "";

    return this.publicProfile.subEspecialidades.map(s => s.objetoDetalle).join(", ");
  }

  getSocialMediaIcon(network:SocialNetworkPublicProfile){
    let iconClasName = "";

    switch (network.tipo) {
      // Facebook
      case 24:
        iconClasName = "phr-iconset-facebook";
        break;
      // Twitter
      case 25:
        iconClasName = "phr-iconset-x";
        break;
      // Youtube
      case 26:
        iconClasName = "phr-iconset-youtube";
        break;
      // Linkedin
      case 27:
        iconClasName = "phr-iconset-linked-in";
        break;
      // Instagram
      case 28:
        iconClasName = "phr-iconset-instagram";
        break;
      // Tiktok
      case 32:
        iconClasName = "phr-iconset-tiktok";
        break;
    }

    return iconClasName;
  }

  onSocialMediaClick(network:SocialNetworkPublicProfile){

    if(isPlatformBrowser(this.platformId)){
      var url = network.url;

      if (url.indexOf("http") == -1)
          url = "http://" + url;
  
      window.open(url, '_blank');
    }
  }

  onWebsiteClick(url: string){
    if(isPlatformBrowser(this.platformId)){
      if (url.indexOf("http") == -1)
          url = "http://" + url;

      window.open(url, '_blank');
    }
  }

  sendMessage() {            
    let data = new DialogData();
    
    data.data = this.publicProfile;

    this.dialog.open(DoctorMessageDialogComponent, {          
      panelClass: 'doc-message-dialog',
      data: data
    });    
  }

  gotoPayment(){
    let dto = new OnlinePaymentModel();
    
    dto.idResponsableServicio = this.publicProfile.id;
    dto.nombreResponsableServicio = this.publicProfile.nombrePersona;
    dto.urlAvatarResponsableServicio = this.publicProfile.ubicacionFoto;
    dto.nickname = this.publicProfile.username;
    dto.especialidadResponsableServicio = this.publicProfile.especialidad;
    dto.idEmpresa = this.publicProfile.idEmpresa;  
    dto.nombreEmpresa = this.baseService.getCliniwebDirectoryCompanyName();
    dto.emailResponsableServicio = this.publicProfile.emailRemitenteNotificacion;  
    dto.idEmpresaResponsableServicio = this.publicProfile.idEmpresa;
    
    dto.perfilPublico = this.publicProfile;

    let routeDialogModel = new PaymentRouteDialogModel();
    routeDialogModel.state = dto;
    
    this.routeDialogService.openDialog(routeDialogModel);
  }

  onAppointmentBtnClick() {
    let container = this.routeDialogService.anyOpen()
      ? this.document.getElementsByClassName("route-dialog-content")[0]
      : this.document.getElementsByClassName("body-container")[0];

    let locationEl = document.querySelector(".appointment-location-container .schedule-container")?.closest(".appointment-location-container");
    let animationContainerEl = locationEl?.querySelector(".appointment-location-container .schedule-container .schedule-content");

    if (!locationEl || !animationContainerEl)
      return;
    
    animationContainerEl.classList.remove("location-animation");
    animationContainerEl.classList.add("location-animation");

    setTimeout(() => {
      if (animationContainerEl)
        animationContainerEl.classList.remove("location-animation");
    }, 3050);

    this.utilsService.scrollToElementWithAnimation(container, locationEl);
  }

  showIconAvatar(){
    return this.publicProfile.tipoPerfil != PublicProfileType.Doctor && !this.publicProfile.ubicacionFoto;
  }

  showImageAvatar(){
    return this.publicProfile.ubicacionFoto;
  }

  showInitialsAvatar(){
    return this.publicProfile.tipoPerfil == PublicProfileType.Doctor && !this.publicProfile.ubicacionFoto;
  }

  getProfileTypeIconClassName(){

    let profileType = this.publicProfile.tipoPerfil;
    let className: string = 'phr-iconset-';

    switch(profileType){
      case PublicProfileType.Doctor:
      default:
        className += 'estetoscopio';
        break;
      case PublicProfileType.Farmacia:
        className += 'farmacia';
        break;
      case PublicProfileType.Clinica:
        className += 'hospital';
        break;
      case PublicProfileType.Fisioterapia:
          className += 'fisioterapia';
          break;
      case PublicProfileType.Laboratorio:
        className += 'laboratorios';
        break;
      case PublicProfileType.CentroDeImagenes:
        className += 'imagenologia';
        break;  
      case PublicProfileType.ServiciosOdontologicos:
        className += 'odontologia';
        break;
      case PublicProfileType.Opticas:
        className += 'open-eye';
        break;
      case PublicProfileType.ClinicasDeAtencionPrimaria:
        className += 'clinic';
        break;
    }

    return className;
  }

  getFeaturesConceptsText(){
    try{
      let featuresConceptsObject:FeatureConceptDto = JSON.parse(this.publicProfile.descuentos[0].objetoDetalle);

      return featuresConceptsObject && featuresConceptsObject.text ? featuresConceptsObject.text : '';
    }
    catch(e){
      return '';
    }
  }

  getFeatureConceptIconClassName(){
    try{
      let featuresConceptsObject:FeatureConceptDto = JSON.parse(this.publicProfile.descuentos[0].objetoDetalle);

      return featuresConceptsObject && featuresConceptsObject.iconClassName ? `phr-iconset-${featuresConceptsObject.iconClassName}` : '';
    }
    catch(e){
      return '';
    }
  }
}
